import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Alert,
  Spinner,
  Card,
  Progress,
} from '@chakra-ui/react'
import { useGmail } from '../hooks/useGmail'

const RANGE_OPTIONS = [
  { value: '7d', label: '1 week' },
  { value: '3m', label: '3 months' },
  { value: '6m', label: '6 months' },
  { value: '1y', label: '1 year' },
  { value: '2y', label: '2 years' },
  { value: '5y', label: '5 years' },
]

export default function Email() {
  const {
    status,
    isLoading,
    connect,
    isConnecting,
    sync,
    isSyncing,
    newEmails,
    disconnect,
    isDisconnecting,
  } = useGmail()

  const [searchParams, setSearchParams] = useSearchParams()
  const [range, setRange] = useState('3m')
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Handle OAuth callback query params
  useEffect(() => {
    const connected = searchParams.get('connected')
    const error = searchParams.get('error')

    if (connected === 'true') {
      setMessage({ type: 'success', text: 'Gmail connected successfully!' })
      setSearchParams({}, { replace: true })
    } else if (error) {
      setMessage({ type: 'error', text: `Gmail connection failed: ${error}` })
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const handleSync = async () => {
    setMessage(null)
    try {
      await sync(range)
      setMessage({ type: 'success', text: 'Sync started...' })
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Sync failed',
      })
    }
  }

  const handleDisconnect = async () => {
    setMessage(null)
    try {
      await disconnect()
      setMessage({ type: 'success', text: 'Gmail disconnected' })
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Disconnect failed',
      })
    }
  }

  if (isLoading) {
    return (
      <Box minH="calc(100vh - 57px)" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="lg" color="blue.500" />
      </Box>
    )
  }

  const connected = status?.connected
  const syncState = status?.syncState

  return (
    <Box minH="calc(100vh - 57px)" bg="gray.50" py={8}>
      <Container maxW="md">
        <VStack gap={6} align="stretch">
          <Box>
            <Heading size="lg" mb={2}>
              Email Sync
            </Heading>
            <Text color="gray.600">
              Connect your Gmail to make emails searchable in Chat.
            </Text>
          </Box>

          {message && (
            <Alert.Root status={message.type === 'success' ? 'success' : 'error'}>
              <Alert.Indicator />
              <Alert.Title>{message.text}</Alert.Title>
            </Alert.Root>
          )}

          {!connected ? (
            <Card.Root>
              <Card.Body>
                <VStack gap={4}>
                  <Text>Connect your Gmail account to sync and search your emails alongside your documents.</Text>
                  <Button
                    colorScheme="blue"
                    onClick={() => connect()}
                    loading={isConnecting}
                    width="full"
                    size="lg"
                  >
                    Connect Gmail
                  </Button>
                </VStack>
              </Card.Body>
            </Card.Root>
          ) : (
            <>
              <Card.Root>
                <Card.Body>
                  <VStack gap={4} align="stretch">
                    <Box>
                      <Text fontWeight="medium" mb={2}>Sync Range</Text>
                      <HStack gap={2} flexWrap="wrap">
                        {RANGE_OPTIONS.map((opt) => (
                          <Button
                            key={opt.value}
                            size="sm"
                            variant={range === opt.value ? 'solid' : 'outline'}
                            colorScheme={range === opt.value ? 'blue' : 'gray'}
                            onClick={() => setRange(opt.value)}
                          >
                            {opt.label}
                          </Button>
                        ))}
                      </HStack>
                    </Box>

                    <Button
                      colorScheme="blue"
                      onClick={handleSync}
                      loading={isSyncing}
                      size="lg"
                    >
                      {isSyncing ? 'Syncing...' : 'Sync Emails'}
                    </Button>
                  </VStack>
                </Card.Body>
              </Card.Root>

              {/* Status card */}
              <Card.Root>
                <Card.Body>
                  <VStack gap={2} align="stretch">
                    <HStack justify="space-between">
                      <Text fontWeight="medium">Status</Text>
                      <Text color="green.600">Connected</Text>
                    </HStack>
                    {syncState && (
                      <>
                        <HStack justify="space-between">
                          <Text color="gray.600">Emails synced</Text>
                          <Text fontWeight="medium">{syncState.email_count}</Text>
                        </HStack>
                        {syncState.last_sync_at && (
                          <HStack justify="space-between">
                            <Text color="gray.600">Last sync</Text>
                            <Text fontSize="sm">{new Date(syncState.last_sync_at).toLocaleString()}</Text>
                          </HStack>
                        )}
                        {syncState.syncing && (
                          <Box>
                            <Text color="blue.600" fontSize="sm" mb={1}>
                              {newEmails
                                ? `Syncing ${newEmails} emails...`
                                : 'Syncing in progress...'}
                            </Text>
                            <Progress.Root
                              value={
                                syncState.total_batches
                                  ? ((syncState.completed_batches || 0) / syncState.total_batches) * 100
                                  : null
                              }
                              size="sm"
                            >
                              <Progress.Track>
                                <Progress.Range />
                              </Progress.Track>
                            </Progress.Root>
                          </Box>
                        )}
                      </>
                    )}
                  </VStack>
                </Card.Body>
              </Card.Root>

              <Button
                variant="outline"
                colorScheme="red"
                onClick={handleDisconnect}
                loading={isDisconnecting}
                size="sm"
              >
                Disconnect Gmail
              </Button>
            </>
          )}

          {/* Instructions */}
          <Card.Root bg="blue.50">
            <Card.Body>
              <Text fontWeight="medium" mb={2}>
                How it works:
              </Text>
              <VStack align="start" gap={1}>
                <Text fontSize="sm">1. Connect your Gmail account</Text>
                <Text fontSize="sm">2. Choose a time range and sync your emails</Text>
                <Text fontSize="sm">3. Go to Chat and ask questions about your emails</Text>
                <Text fontSize="sm">4. Re-sync anytime to pick up new emails</Text>
              </VStack>
            </Card.Body>
          </Card.Root>
        </VStack>
      </Container>
    </Box>
  )
}
